import React, { useEffect, useRef, useState } from 'react';

import { equals, isEmpty } from 'ramda';
import { FormSpy } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import { useSnackbar } from 'hooks/useSnackbar';
import { setAppHasChangesAction } from 'store/unsavedChanges';
import { appHasChangesSelector } from 'store/unsavedChanges/selectors';
import { guestsFormsHeaderId } from 'utils/itineraries/guestPortal/prepareGuestPortal';
import logger from 'utils/logger';

import { SaveIndicator } from '../shared/SaveIndicator';

function useFirstRender() {
  const firstRender = useRef(true);

  useEffect(() => {
    firstRender.current = false;
  }, []);

  return firstRender.current;
}

const diff = (values, prevValues) => {
  return Object.keys(values).reduce((acc, key) => {
    if (values[key] instanceof Array) {
      const filtered = values[key].filter((obj, idx) => !equals(obj, prevValues[key][idx]));
      return isEmpty(filtered) ? acc : { ...acc, [key]: filtered };
    }

    if (values[key] !== prevValues[key]) {
      return { ...acc, [key]: values[key] };
    }

    return acc;
  }, {});
};

const AutoSave = ({ save, values, debounce }) => {
  const promiseRef = useRef();
  const timeoutRef = useRef();
  const [prevValues, setPrevValues] = useState(values);
  const dispatch = useDispatch();
  const firstRender = useFirstRender();
  const appHasChanges = useSelector(appHasChangesSelector);
  const onSnackbar = useSnackbar();

  const handleSave = async () => {
    if (promiseRef.current) {
      await promiseRef.current;
    }

    const difference = diff(values, prevValues);
    if (!isEmpty(difference)) {
      dispatch(setAppHasChangesAction(true));
      setPrevValues(values);
      promiseRef.current = save(difference);
      try {
        await promiseRef.current;
      } catch (err) {
        logger.error(err);
        onSnackbar("We couldn't save your changes. Please try again later.", true);
      } finally {
        promiseRef.current = null;
        dispatch(setAppHasChangesAction(false));
      }
    } else {
      dispatch(setAppHasChangesAction(false));
    }
  };

  useEffect(() => {
    if (firstRender) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    dispatch(setAppHasChangesAction(true));
    timeoutRef.current = setTimeout(handleSave, debounce);
  }, [values]);

  return <SaveIndicator appHasChanges={appHasChanges} mountId={guestsFormsHeaderId} />;
};

export default (props) => <FormSpy {...props} subscription={{ values: true }} component={AutoSave} />;
